import { useCallback, useRef, useState } from "react";
import type {
  SetResolutionCommand,
  SetFpsCommand,
  SetBitrateCommand,
  SetVolumeCommand,
  CommandMessage,
} from "../types/commands";

// ─── Types ───────────────────────────────────────────────────────────

/** Resolution preset: label + dimensions. */
interface ResolutionPreset {
  label: string;
  width: number;
  height: number;
}

/** Bitrate preset: label + kbps value. */
interface BitratePreset {
  label: string;
  value: number;
}

/** Props for the ControlPanel component. */
export interface ControlPanelProps {
  /** Send a command via the reliable DataChannel. */
  onCommand: (cmd: CommandMessage) => void;
  /** Callback for fullscreen toggle. */
  onToggleFullscreen?: () => void;
  /** Whether the player is currently fullscreen. */
  isFullscreen?: boolean;
  /** Disable all controls (e.g. when disconnected). */
  disabled?: boolean;
}

// ─── Presets ─────────────────────────────────────────────────────────

const RESOLUTION_PRESETS: ResolutionPreset[] = [
  { label: "1080p (1920×1080)", width: 1920, height: 1080 },
  { label: "720p (1280×720)", width: 1280, height: 720 },
  { label: "XGA (1024×768)", width: 1024, height: 768 },
  { label: "SVGA (800×600)", width: 800, height: 600 },
];

const BITRATE_PRESETS: BitratePreset[] = [
  { label: "Low (1 Mbps)", value: 1000 },
  { label: "Medium (2.5 Mbps)", value: 2500 },
  { label: "High (5 Mbps)", value: 5000 },
  { label: "Ultra (8 Mbps)", value: 8000 },
];

const FPS_MIN = 10;
const FPS_MAX = 60;
const FPS_STEP = 5;
const FPS_DEFAULT = 30;

const VOLUME_MIN = 0;
const VOLUME_MAX = 100;
const VOLUME_DEFAULT = 70;

// ─── Component ───────────────────────────────────────────────────────

export default function ControlPanel({
  onCommand,
  onToggleFullscreen,
  isFullscreen = false,
  disabled = false,
}: ControlPanelProps) {
  const [resolution, setResolution] = useState(0); // index into RESOLUTION_PRESETS
  const [fps, setFps] = useState(FPS_DEFAULT);
  const [bitrate, setBitrate] = useState(1); // index into BITRATE_PRESETS
  const [volume, setVolume] = useState(VOLUME_DEFAULT);
  const [muted, setMuted] = useState(false);

  // ── Resolution ─────────────────────────────────────────────────

  const handleResolution = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const idx = Number(e.target.value);
      const preset = RESOLUTION_PRESETS[idx];
      if (!preset) return;
      setResolution(idx);
      const cmd: SetResolutionCommand = {
        cmd: "set_resolution",
        width: preset.width,
        height: preset.height,
      };
      onCommand(cmd);
    },
    [onCommand],
  );

  // ── FPS ────────────────────────────────────────────────────────

  const fpsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleFps = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = Number(e.target.value);
      setFps(value);
      // Debounce: only send after 300ms of no change
      if (fpsTimerRef.current) clearTimeout(fpsTimerRef.current);
      fpsTimerRef.current = setTimeout(() => {
        const cmd: SetFpsCommand = { cmd: "set_fps", fps: value };
        onCommand(cmd);
      }, 300);
    },
    [onCommand],
  );

  // ── Bitrate ────────────────────────────────────────────────────

  const handleBitrate = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const idx = Number(e.target.value);
      const preset = BITRATE_PRESETS[idx];
      if (!preset) return;
      setBitrate(idx);
      const cmd: SetBitrateCommand = {
        cmd: "set_bitrate",
        bitrate_kbps: preset.value,
      };
      onCommand(cmd);
    },
    [onCommand],
  );

  // ── Volume ─────────────────────────────────────────────────────

  const handleVolume = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = Number(e.target.value);
      setVolume(value);
      if (muted) setMuted(false);
      const cmd: SetVolumeCommand = {
        cmd: "set_volume",
        volume: value / 100,
      };
      onCommand(cmd);
    },
    [onCommand, muted],
  );

  const handleMute = useCallback(() => {
    const next = !muted;
    setMuted(next);
    const cmd: SetVolumeCommand = {
      cmd: "set_volume",
      volume: next ? 0 : volume / 100,
    };
    onCommand(cmd);
  }, [onCommand, muted, volume]);

  // ── Fullscreen ─────────────────────────────────────────────────

  const handleFullscreen = useCallback(() => {
    onToggleFullscreen?.();
  }, [onToggleFullscreen]);

  // ── Render ─────────────────────────────────────────────────────

  return (
    <div
      role="toolbar"
      aria-label="Stream controls"
      style={styles.panel}
    >
      {/* Resolution */}
      <select
        id="ctrl-resolution"
        value={resolution}
        onChange={handleResolution}
        disabled={disabled}
        aria-label="Select resolution"
        style={styles.select}
      >
        {RESOLUTION_PRESETS.map((p, i) => (
          <option key={p.label} value={i}>
            {p.label}
          </option>
        ))}
      </select>

      {/* FPS */}
      <div style={styles.sliderGroup}>
        <span style={styles.label}>FPS {fps}</span>
        <input
          id="ctrl-fps"
          type="range"
          min={FPS_MIN}
          max={FPS_MAX}
          step={FPS_STEP}
          value={fps}
          onChange={handleFps}
          disabled={disabled}
          aria-label={`Frames per second: ${fps}`}
          style={styles.slider}
        />
      </div>

      {/* Volume */}
      <div style={styles.sliderGroup}>
        <span style={styles.label}>Vol {muted ? "Off" : `${volume}%`}</span>
        <input
          id="ctrl-volume"
          type="range"
          min={VOLUME_MIN}
          max={VOLUME_MAX}
          value={muted ? 0 : volume}
          onChange={handleVolume}
          disabled={disabled}
          aria-label={`Volume: ${muted ? "muted" : `${volume}%`}`}
          style={styles.slider}
        />
        <button
          type="button"
          onClick={handleMute}
          disabled={disabled}
          aria-label={muted ? "Unmute audio" : "Mute audio"}
          aria-pressed={muted}
          style={styles.muteBtn}
        >
          {muted ? "\u{1F507}" : "\u{1F50A}"}
        </button>
      </div>

      {/* Bitrate */}
      <select
        id="ctrl-bitrate"
        value={bitrate}
        onChange={handleBitrate}
        disabled={disabled}
        aria-label="Select bitrate"
        style={styles.select}
      >
        {BITRATE_PRESETS.map((p, i) => (
          <option key={p.label} value={i}>
            {p.label}
          </option>
        ))}
      </select>

      {/* Fullscreen */}
      {onToggleFullscreen && (
        <button
          type="button"
          onClick={handleFullscreen}
          disabled={disabled}
          aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
          aria-pressed={isFullscreen}
          style={styles.muteBtn}
        >
          {isFullscreen ? "\u{2716}" : "\u{26F6}"}
        </button>
      )}
    </div>
  );
}

// ─── Inline styles (no external CSS dependency) ──────────────────────

const styles: Record<string, React.CSSProperties> = {
  panel: {
    display: "flex",
    flexWrap: "nowrap",
    gap: "8px",
    alignItems: "center",
    fontFamily: "system-ui, -apple-system, sans-serif",
    fontSize: "12px",
    color: "#e0e0e0",
  },
  sliderGroup: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  label: {
    fontSize: "10px",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.3px",
    color: "#8888aa",
    whiteSpace: "nowrap",
  },
  select: {
    padding: "3px 6px",
    borderRadius: "4px",
    border: "1px solid #333",
    background: "#1a1a2e",
    color: "#e0e0e0",
    fontSize: "11px",
    cursor: "pointer",
  },
  slider: {
    width: "70px",
    accentColor: "#4361ee",
    cursor: "pointer",
  },
  muteBtn: {
    padding: "2px 6px",
    borderRadius: "4px",
    border: "1px solid #333",
    background: "#1a1a2e",
    color: "#e0e0e0",
    cursor: "pointer",
    fontSize: "12px",
    lineHeight: 1,
  },
};
